import { useSuspenseQuery } from '@apollo/client';
import {
  type CellContext,
  type HeaderContext,
  useReactTable,
} from '@tanstack/react-table';
import sortBy from 'lodash/sortBy';
import React, { useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import type { BulkEntityFilters } from '@eluve/api-contract';
import {
  ExportableAppointmentStatuses,
  BulkEntitySelectionMode as SelectionMode,
} from '@eluve/api-contract';
import {
  ComputedAppointmentStatusLabel,
  ComputedAppointmentStatuses,
  computedAppointmentStatusAccessorFn,
} from '@eluve/blocks';
import {
  Button,
  Checkbox,
  ColDefBuilder,
  DataTableFilterField,
  ExtendedSortingState,
  Icon,
  Link,
  Option,
  ServerDataTable,
  Stack,
  Text,
  useDataTableForQuery,
  useResetDataTableRowSelectionOnStateChange,
} from '@eluve/components';
import { convertUrlStringDate } from '@eluve/date-utils';
import { AppointmentsBoolExp, AppointmentsOrderBy } from '@eluve/graphql-types';
import { ResultOf, graphql } from '@eluve/graphql.tada';
import {
  useAssignedTenantIdFromParams,
  useIsTenantAdminFromSession,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';
import { formatHumanName, getUserName } from '@eluve/utils';

import { DownloadChartsPdfButton } from './DownloadChartsPdfButton';
import { ViewAppointmentSuperbillButton } from './ViewAppointmentSuperbillButton';

const SELECTION_COLUMN_ID = 'rowSelection';
const SUPERBILL_COLUMN_ID = 'superBillFileUrl';
const PATIENT_COLUMN_ID = 'patientName';

/**
 * Retrieves necessary backend values to build filter fields to be used in the table
 */
const appointmentsFilterOptionsQuery = graphql(`
  query appointmentsFilterOptions($tenantId: uuid!) {
    tenantsByPk(id: $tenantId) {
      __typename
      id
      locations {
        __typename
        id
        name
      }
      tenantUsers {
        __typename
        tenantId
        userId
        user {
          __typename
          id
          firstName
          lastName
          email
        }
      }
    }
  }
`);

const searchTenantAppointmentsQuery = graphql(`
  query searchTenantAppointments(
    $filter: AppointmentsBoolExp
    $offset: Int
    $orderBy: [AppointmentsOrderBy!]
    $limit: Int
    $includeSuperbillFiles: Boolean = false
  ) {
    appointments(
      where: $filter
      offset: $offset
      limit: $limit
      orderBy: $orderBy
    ) {
      __typename
      tenantId
      id
      name
      startDate
      statusLabel
      createdAt
      updatedAt
      user {
        __typename
        id
        firstName
        lastName
        email
      }
      signedSuperbills: appointmentFiles(
        where: { fileType: { _eq: SUPERBILL_PDF } }
        orderBy: { createdAt: DESC }
        limit: 1
      ) @include(if: $includeSuperbillFiles) {
        __typename
        appointmentId
        fileId
        userUpload {
          __typename
          id
          fileUrl
        }
      }
      location {
        __typename
        id
        name
      }
      patient {
        __typename
        id
        firstName
        lastName
      }
    }
    appointmentsAggregate(where: $filter) {
      aggregate {
        count
      }
    }
  }
`);
type TableRow = {
  tenantId: string;
  id: string;
  name: string;
  statusLabel: string;
  locationId: string | null;
  locationName: string | null;
  updatedAt: string;
  startDate: string | null;
  userId: string;
  userName: string;
  patientId: string | null;
  patientName: string | null;
  superBillFileUrl?: string | null;
  isAdmin?: boolean;
};

const convertToRows = (
  results: ResultOf<typeof searchTenantAppointmentsQuery>,
  { isAdmin }: { isAdmin: boolean },
): TableRow[] => {
  return (results?.appointments ?? []).map<TableRow>((a) => {
    return {
      tenantId: a.tenantId,
      id: a.id,
      name: a.name,
      locationId: a.location?.id ?? null,
      locationName: a.location?.name ?? null,
      statusLabel: a.statusLabel ?? '',
      startDate: a.startDate ?? null,
      // TODO(jesse)[ELU-4002] Use last interacted with date from query
      updatedAt: a.updatedAt,
      userId: a.user?.id ?? '',
      userName: getUserName({
        firstName: a.user?.firstName,
        lastName: a.user?.lastName,
        email: a.user?.email,
      }),
      patientId: a.patient?.id ?? null,
      patientName: formatHumanName(a.patient?.firstName, a.patient?.lastName),
      superBillFileUrl: a.signedSuperbills?.[0]?.userUpload?.fileUrl ?? null,
      isAdmin,
    };
  });
};

const SelectionHeader = ({ table }: HeaderContext<TableRow, unknown>) => {
  return (
    <Stack justify="center" gap={2} direction="horizontal">
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected()
            ? true
            : table.getIsSomePageRowsSelected()
              ? 'indeterminate'
              : false
        }
        onCheckedChange={() => {
          const isAllSelected = table.getIsAllPageRowsSelected();
          const hasSomeSelected = table.getIsSomePageRowsSelected();
          if (isAllSelected || hasSomeSelected) {
            table.toggleAllPageRowsSelected(false);
          } else {
            table.toggleAllPageRowsSelected(true);
          }
        }}
        aria-label="Select all"
        className="data-[state=indeterminate]:border-text-primary data-[state=indeterminate]:bg-text-primary data-[state=indeterminate]:text-bg-tertiary"
      />
    </Stack>
  );
};

const SelectionCell = ({ row }: CellContext<TableRow, unknown>) => (
  <Stack justify="center" gap={2} direction="horizontal">
    <Checkbox
      checked={row.getIsSelected()}
      onCheckedChange={() => row.toggleSelected()}
      aria-label="Select row"
      className="opacity-0 group-hover:opacity-100 hover:border-border-quaternary focus-visible:opacity-100 data-[state=checked]:opacity-100"
    />
  </Stack>
);

const appointmentLink = ({ tenantId, id, isAdmin }: TableRow) =>
  isAdmin
    ? `/admin/organizations/${tenantId}/appointments/${id}`
    : `/organizations/${tenantId}/appointments/${id}`;

export const columns = new ColDefBuilder<TableRow>()
  .colDef({
    id: SELECTION_COLUMN_ID,
    header: SelectionHeader,
    cell: SelectionCell,
    enableSorting: false,
    enableHiding: false,
    size: 32,
  })
  .detailsLink(appointmentLink, 'Details')
  .linkSortable('name', appointmentLink)
  .colDef({
    id: PATIENT_COLUMN_ID,
    header: 'Patient',
    cell: ({ row }) =>
      row.original.patientId ? (
        <Link
          to={`/organizations/${row.original.tenantId}/patients/${row.original.patientId}`}
        >
          <span className="privacy-text">{row.original.patientName}</span>
        </Link>
      ) : null,
  })
  .defaultSortable('userId', {
    label: 'User',
    cellRenderer: ({ userName }) => {
      return <span>{userName}</span>;
    },
  })
  .dateSortable('updatedAt', 'Last Modified')
  .dateSortable('startDate', 'Start Date')
  .defaultSortable('locationId', {
    label: 'Location',
    cellRenderer: ({ locationName }) => {
      return <span>{locationName}</span>;
    },
  })
  .defaultSortable('statusLabel', {
    label: 'Status',
    cellRenderer: ({ statusLabel }) => {
      return <ComputedAppointmentStatusLabel statusLabel={statusLabel} />;
    },
  })
  .colDef({
    id: SUPERBILL_COLUMN_ID,
    header: 'Artifacts',
    cell: ({
      row: {
        original: { superBillFileUrl, tenantId, id },
      },
    }) => {
      return superBillFileUrl ? (
        <ViewAppointmentSuperbillButton
          tenantId={tenantId}
          appointmentId={id}
        />
      ) : null;
    },
  })
  .build();

const defaultSort: ExtendedSortingState<TableRow> = [
  {
    id: 'updatedAt',
    desc: true,
  },
];

export interface FilterableAppointmentsTableProps {
  baseFilterConditions?: AppointmentsBoolExp[];
  includeSuperbillFiles?: boolean;
  isAdmin?: boolean;
  hidePatientColumn?: boolean;
  enableRowSelection?: boolean;
}
export const FilterableAppointmentsTable: React.FC<
  FilterableAppointmentsTableProps
> = ({
  baseFilterConditions = [],
  includeSuperbillFiles = false,
  isAdmin = false,
  hidePatientColumn = false,
  enableRowSelection = false,
}) => {
  const userId = useUserIdFromSession();
  const isTenantAdmin = useIsTenantAdminFromSession();
  const tenantId = useAssignedTenantIdFromParams();
  const isAllPatientAccessEnabled =
    useIsFeatureFlagEnabled('ALL_PATIENT_ACCESS');

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [isSelectAllMode, setIsSelectAllMode] = useState(false);

  const showUserFilter = isTenantAdmin || isAllPatientAccessEnabled;
  const {
    data: { tenantsByPk: tenant },
  } = useSuspenseQuery(appointmentsFilterOptionsQuery, {
    variables: { tenantId },
  });
  const filterFields = useMemo(() => {
    const fields: DataTableFilterField<TableRow>[] = [
      {
        id: 'locationId',
        label: 'Location',
        options: tenant?.locations.map((l) => ({
          label: l.name,
          value: l.id,
        })),
      },
      {
        id: 'statusLabel',
        label: 'Status',
        options: ComputedAppointmentStatuses.map((s) => ({
          label: computedAppointmentStatusAccessorFn(s),
          value: s,
        })),
      },
    ];
    // Only include the ability to filter on users if the user is a tenant admin or has all patient access
    if (showUserFilter) {
      const sortedTenantUsers = sortBy(tenant?.tenantUsers ?? [], (tu) => {
        return getUserName({
          firstName: tu.user?.firstName,
          lastName: tu.user?.lastName,
          email: tu.user?.email,
        }).toLowerCase();
      });

      const userFilter: DataTableFilterField<TableRow> = {
        id: 'userId',
        label: 'User',
        options: sortedTenantUsers.map((tu): Option => {
          const userName = getUserName({
            firstName: tu.user?.firstName,
            lastName: tu.user?.lastName,
            email: tu.user?.email,
          });
          const isCurrentUser = tu.userId === userId;
          return {
            label: isCurrentUser ? `${userName} (You)` : userName,
            value: tu.userId,
          };
        }),
      };

      fields.push(userFilter);
    }

    // date range filter for start date
    const dateRangeFilter: DataTableFilterField<TableRow> = {
      id: 'startDate',
      label: 'Start Date',
      type: 'dateRange',
      placeholder: 'Start Date',
    };

    fields.push(dateRangeFilter);
    return fields;
  }, [tenant, showUserFilter, userId]);

  const {
    rows: serverRows,
    reactTableOptions,
    isPending,
    data,
  } = useDataTableForQuery({
    query: searchTenantAppointmentsQuery,
    convertToRows: (result) => convertToRows(result, { isAdmin }),
    filterFields,
    defaultSort,
    convertSearchParamsToVariables: ({ offset, limit, sorting, filters }) => {
      const filterConditions = [...baseFilterConditions];
      if (filters.locationId && filters.locationId.length) {
        filterConditions.push({
          locationId: {
            _in: filters.locationId as string[],
          },
        });
      }
      if (filters.statusLabel && filters.statusLabel.length) {
        filterConditions.push({
          statusLabel: {
            _in: filters.statusLabel as string[],
          },
        });
      }
      if (!showUserFilter) {
        filterConditions.push({
          userId: { _eq: userId },
        });
      } else {
        if (filters.userId && filters.userId.length) {
          filterConditions.push({
            userId: {
              _in: filters.userId as string[],
            },
          });
        }
      }

      // handle date range filter
      if (filters.startDate && typeof filters.startDate === 'string') {
        try {
          // parse underscore-separated date format: "YYYY-MM-DD_YYYY-MM-DD" or "YYYY-MM-DD_" or "_YYYY-MM-DD"
          const [fromStr, toStr] = filters.startDate.split('_');
          const dateConditions: AppointmentsBoolExp = {};
          if (fromStr) {
            const fromDate = convertUrlStringDate(fromStr);
            if (fromDate) {
              const startOfDay = new Date(fromDate);
              startOfDay.setHours(0, 0, 0, 0);
              dateConditions.startDate = {
                _gte: startOfDay.toISOString(),
              };
            }
          }
          if (toStr) {
            const toDate = convertUrlStringDate(toStr);
            if (toDate) {
              const endOfDay = new Date(toDate);
              endOfDay.setHours(23, 59, 59, 999);
              dateConditions.startDate = {
                ...dateConditions.startDate,
                _lte: endOfDay.toISOString(),
              };
            }
          }
          if (dateConditions.startDate) {
            filterConditions.push(dateConditions);
          }
        } catch {
          // invalid date format, ignore
        }
      }

      return {
        includeSuperbillFiles,
        filter: {
          _and: filterConditions,
        },
        offset,
        limit,
        orderBy:
          sorting && sorting.length
            ? sorting.map(({ desc, id }) => ({
                ...match<keyof TableRow, AppointmentsOrderBy>(id)
                  .with('userId', () => ({
                    user: {
                      email: desc ? 'DESC' : 'ASC',
                    },
                  }))
                  .with('locationId', () => ({
                    location: {
                      name: desc ? 'DESC' : 'ASC',
                    },
                  }))
                  .otherwise(() => ({
                    [id]: desc ? 'DESC' : 'ASC',
                  })),
              }))
            : null,
      };
    },
  });
  const rowCount = data?.appointmentsAggregate?.aggregate?.count;

  const mergedColumnDefinitions = useMemo(() => {
    let mergedColumns = [...columns];
    if (!enableRowSelection) {
      mergedColumns = mergedColumns.filter(
        (f) =>
          (f as unknown as Record<string, unknown>)?.id !== SELECTION_COLUMN_ID,
      );
    }
    if (!showUserFilter) {
      mergedColumns = mergedColumns.filter(
        (f) =>
          (f as unknown as Record<string, unknown>)?.accessorKey !== 'userId',
      );
    }
    if (!includeSuperbillFiles) {
      mergedColumns = mergedColumns.filter(
        (f) =>
          (f as unknown as Record<string, unknown>)?.id !== SUPERBILL_COLUMN_ID,
      );
    }
    if (hidePatientColumn) {
      mergedColumns = mergedColumns.filter(
        (f) =>
          (f as unknown as Record<string, unknown>)?.id !== PATIENT_COLUMN_ID,
      );
    }
    return mergedColumns;
  }, [
    showUserFilter,
    includeSuperbillFiles,
    hidePatientColumn,
    enableRowSelection,
  ]);

  const table = useReactTable({
    ...reactTableOptions,
    data: serverRows,
    columns: mergedColumnDefinitions,
    rowCount,
    getRowId: (r) => r.id,
    enableRowSelection,
    state: enableRowSelection
      ? { ...reactTableOptions.state, rowSelection }
      : reactTableOptions.state,
    onRowSelectionChange: enableRowSelection ? setRowSelection : undefined,
  });

  useResetDataTableRowSelectionOnStateChange({
    table,
    pagination: reactTableOptions.state?.pagination,
    sorting: reactTableOptions.state?.sorting,
    columnFilters: reactTableOptions.state?.columnFilters,
  });

  const showJustMyAppointments = () =>
    table.getColumn('userId')?.setFilterValue([userId]);

  const selectedRowIds = useMemo(() => {
    return Object.keys(rowSelection).filter((key) => rowSelection[key]);
  }, [rowSelection]);

  const allSelectedNonExportable = useMemo(() => {
    if (selectedRowIds.length === 0) return false;
    const exportableStatuses = new Set<string>(ExportableAppointmentStatuses);
    return selectedRowIds.every((id) => {
      const row = serverRows.find((r) => r.id === id);
      return row && !exportableStatuses.has(row.statusLabel);
    });
  }, [selectedRowIds, serverRows]);

  // Extract current filter values from table state
  const currentFilters = useMemo(() => {
    const columnFilters = reactTableOptions.state?.columnFilters ?? [];
    const filters: BulkEntityFilters = {};

    // Extract filters from columnFilters
    columnFilters.forEach((filter) => {
      if (filter.id === 'locationId' && Array.isArray(filter.value)) {
        filters.locationId = filter.value as string[];
      } else if (filter.id === 'statusLabel' && Array.isArray(filter.value)) {
        filters.statusLabel = filter.value as string[];
      } else if (filter.id === 'userId' && Array.isArray(filter.value)) {
        filters.userId = filter.value as string[];
      } else if (
        filter.id === 'startDate' &&
        typeof filter.value === 'string'
      ) {
        // parse underscore-separated date format: "YYYY-MM-DD_YYYY-MM-DD" or "YYYY-MM-DD_" or "_YYYY-MM-DD"
        const [fromStr, toStr] = filter.value.split('_');
        if (fromStr) {
          filters.startDateFrom = fromStr;
        }
        if (toStr) {
          filters.startDateTo = toStr;
        }
      }
    });

    // Extract patientId from baseFilterConditions
    const patientIds = new Set<string>();
    baseFilterConditions.forEach((condition) => {
      if (condition.patientId?._eq) {
        patientIds.add(condition.patientId._eq);
      } else if (condition.patientId?._in) {
        condition.patientId._in.forEach((id) => patientIds.add(id));
      }
    });
    if (patientIds.size > 0) {
      filters.patientId = Array.from(patientIds);
    }

    // If user filter is not shown, automatically add current userId
    if (!showUserFilter && userId) {
      filters.userId = [userId];
    }

    return filters;
  }, [
    reactTableOptions.state?.columnFilters,
    baseFilterConditions,
    showUserFilter,
    userId,
  ]);

  const selectionMode = isSelectAllMode
    ? SelectionMode.FILTER
    : SelectionMode.EXPLICIT;

  const pageSize = table.getState().pagination.pageSize;
  const isAllPageRowsSelected = table.getIsAllPageRowsSelected();
  const hasMorePages = (rowCount ?? 0) > pageSize;
  const hasActiveFilters = Boolean(
    reactTableOptions.state?.columnFilters?.length,
  );
  const shouldShowSelectAllBanner =
    enableRowSelection &&
    isAllPageRowsSelected &&
    hasMorePages &&
    hasActiveFilters;

  const handleSelectAll = () => {
    setIsSelectAllMode(true);
  };

  const handleClearSelection = () => {
    setIsSelectAllMode(false);
    table.toggleAllPageRowsSelected(false);
    setRowSelection({});
  };

  // reset select all mode when the banner is hidden
  useEffect(() => {
    if (!shouldShowSelectAllBanner && isSelectAllMode) {
      setIsSelectAllMode(false);
    }
  }, [shouldShowSelectAllBanner, isSelectAllMode]);

  const selectedRowCount = isSelectAllMode
    ? (table.options.rowCount ?? 0)
    : table.getSelectedRowModel().rows.length;

  return (
    <Stack gap={2}>
      {shouldShowSelectAllBanner && (
        <Stack
          fullWidth={false}
          className="rounded-lg bg-bg-secondary p-3 pl-4"
          gap={2}
          direction="horizontal"
        >
          {!isSelectAllMode ? (
            <>
              <Text>
                {'All '}
                <Text inline weight="bold">
                  {pageSize}
                </Text>
                {` appointments on this page are selected. Select all ${rowCount} filtered appointments?`}
              </Text>
              <Button variant="outline" size="sm" onClick={handleSelectAll}>
                Select All
              </Button>
            </>
          ) : (
            <>
              <Text>
                {'All '}
                <Text weight="bold">{rowCount}</Text>
                {' filtered appointments are selected.'}
              </Text>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearSelection}
              >
                Clear Selection
              </Button>
            </>
          )}
        </Stack>
      )}
      <Stack className="rounded-md border p-2" gap={3}>
        <ServerDataTable<TableRow>
          filterFields={filterFields}
          table={table}
          isPending={isPending}
          toolbarLeftActions={
            <Stack fullWidth={false} gap={2} direction="horizontal">
              {selectedRowCount > 0 && (
                <Text inline className="inline-block w-24 px-2">
                  {selectedRowCount} selected
                </Text>
              )}
              <DownloadChartsPdfButton
                tenantId={tenantId}
                appointmentIds={selectedRowIds}
                selectionMode={selectionMode}
                filters={currentFilters}
                disabled={allSelectedNonExportable}
              />
              {showUserFilter && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={showJustMyAppointments}
                >
                  <Icon name="User" size="auto" />
                  Only my appointments
                </Button>
              )}
            </Stack>
          }
        />
      </Stack>
    </Stack>
  );
};
